import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@nexus/ui";
import { promptVariableUsagesQueryOptions } from "@/lib/queries";

interface VariableUsageButtonProps {
  name: string;
  onOpenPrompt: (promptId: string) => void;
  onOpenReplace: (find: string) => void;
}

export function VariableUsageButton({
  name,
  onOpenPrompt,
  onOpenReplace,
}: VariableUsageButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data: usages = [] } = useQuery(promptVariableUsagesQueryOptions(name));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="cursor-pointer rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <span className="font-mono">{`{{${name}}}`}</span>
          <span className="ml-1.5 text-muted-foreground">
            {t("prompts.usages.usedIn", "Used in {{count}} prompts", { count: usages.length })}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <p className="px-1 pb-1 text-[11px] text-muted-foreground">
          {t("prompts.usages.usedIn", "Used in {{count}} prompts", { count: usages.length })}
        </p>
        <ul className="max-h-48 space-y-0.5 overflow-y-auto">
          {usages.map((usage) => (
            <li key={usage.id}>
              <button
                type="button"
                className="flex w-full cursor-pointer flex-col items-start rounded-sm px-1 py-1 text-left hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                onClick={() => {
                  setOpen(false);
                  onOpenPrompt(usage.id);
                }}
              >
                <span className="text-[13px]">{usage.name}</span>
                <span className="font-mono text-[11px] text-muted-foreground">{usage.usedIn}</span>
              </button>
            </li>
          ))}
        </ul>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2 w-full"
          onClick={() => {
            setOpen(false);
            onOpenReplace(`{{${name}}}`);
          }}
        >
          {t("prompts.usages.openReplace", "Search & replace")}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
